import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import superAdminService from '../services/superAdminService';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Chip,
  TextField, 
  Select, 
  MenuItem, 
  Snackbar,
  Dialog,           
  DialogTitle,      
  DialogContent,    
  DialogContentText, 
  DialogActions 
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import ImageIcon from '@mui/icons-material/Image';
import PersonIcon from '@mui/icons-material/Person';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CampaignIcon from '@mui/icons-material/Campaign';

import { API_BASE_URL } from '../config';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [broadcastType, setBroadcastType] = useState('info');
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await superAdminService.getStats();
        setStats(data);
      } catch (err) {
        console.error('Error fetching stats:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress sx={{ color: '#a8851d' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const statCards = [
    {
      title: 'Total Dealers',
      value: stats?.totalDealers || 0,
      icon: <BusinessIcon sx={{ fontSize: 40, color: '#a8851d' }} />,
      path: '/super-admin/dealers',
      color: '#fff3e0'
    },
    {
      title: 'Total Users',
      value: stats?.totalUsers || 0,
      icon: <PeopleIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
      path: null,
      color: '#e3f2fd'
    },
    {
      title: 'Total Surveys',
      value: stats?.totalSurveys || 0,
      icon: <AssignmentIcon sx={{ fontSize: 40, color: '#388e3c' }} />,
      path: '/super-admin/surveys',
      color: '#e8f5e9'
    },
    {
      title: 'Total Responses',
      value: stats?.totalResponses || 0,
      icon: <QuestionAnswerIcon sx={{ fontSize: 40, color: '#f57c00' }} />,
      path: '/super-admin/responses',
      color: '#fff3e0'
    },
    {
      title: 'Total Images',
      value: stats?.totalImages || 0,
      icon: <ImageIcon sx={{ fontSize: 40, color: '#7b1fa2' }} />,
      path: '/super-admin/images',
      color: '#f3e5f5'
    },
    {
      title: 'Total Customers',
      value: stats?.totalCustomers || 0,
      icon: <PersonIcon sx={{ fontSize: 40, color: '#c2185b' }} />,
      path: null,
      color: '#fce4ec'
    }
  ];

  const showSnackbar = (message, severity = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };
  const handleSendBroadcast = async () => {
      if(!broadcastMsg) return;
      setConfirmDialogOpen(true);
  };

  const confirmSendBroadcast = async () => {
    setConfirmDialogOpen(false);
    setBroadcastLoading(true);
    
    try {
      const token = localStorage.getItem('authToken');
      await fetch(`${API_BASE_URL}/SuperAdmin/broadcast`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({ 
          message: broadcastMsg, 
          type: broadcastType,
          expiresInHours: 48 
        })
      });
      showSnackbar("Broadcast Sent!", "success");
      setBroadcastMsg('');
    } catch (error) {
      showSnackbar("Failed to send", "error");
    } finally {
      setBroadcastLoading(false);
    }
  };

  const handleClearBroadcast = () => {
    setClearDialogOpen(true);
};

const confirmClearBroadcast = async () => {
    setClearDialogOpen(false);
    const token = localStorage.getItem('authToken');
    await fetch(`${API_BASE_URL}/SuperAdmin/broadcast/clear`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
    });
    showSnackbar("Banner Removed.", "success");
};

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
            SuperAdmin Dashboard
          </Typography>
          <Chip 
            label="SUPERADMIN" 
            sx={{ 
              bgcolor: '#a8851d', 
              color: 'white', 
              fontWeight: 600,
              fontSize: '0.75rem'
            }} 
          />
        </Box>

        <Button
            variant="outlined"
            startIcon={<DashboardIcon />}
            onClick={() => navigate('/dashboard')}
            sx={{ 
                color: '#1d4ed8', 
                borderColor: '#1d4ed8', 
                '&:hover': {
                    bgcolor: 'rgba(29, 78, 216, 0.04)',
                    borderColor: '#1e40af'
                } 
            }}
          >
            View Dealer Dashboard
          </Button>
        <Typography variant="body1" sx={{ color: '#666' }}>
          System-wide overview and management
        </Typography>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={3}>
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card 
              sx={{ 
                height: '100%',
                bgcolor: card.color,
                cursor: card.path ? 'pointer' : 'default',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': card.path ? {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.15)'
                } : {}
              }}
              onClick={() => card.path && navigate(card.path)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                      {card.value.toLocaleString()}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#666', fontWeight: 500 }}>
                      {card.title}
                    </Typography>
                    {card.path && (
                      <Button
                        endIcon={<ArrowForwardIcon />}
                        sx={{ mt: 2, color: '#a8851d', fontWeight: 600 }}
                        size="small"
                      >
                        View All
                      </Button>
                    )}
                  </Box>
                  <Box>{card.icon}</Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* BROADCAST SECTION */}
      <Box sx={{ mt: 4, p: 3, bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CampaignIcon color="primary" />
            <Typography variant="h6">System Broadcast</Typography>
        </Box>
        
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
                <TextField 
                    fullWidth 
                    placeholder="Type announcement here..." 
                    size="small"
                    value={broadcastMsg}
                    onChange={(e) => setBroadcastMsg(e.target.value)}
                />
            </Grid>
            <Grid item xs={6} md={2}>
                <Select 
                    fullWidth 
                    size="small" 
                    value={broadcastType}
                    onChange={(e) => setBroadcastType(e.target.value)}
                >
                    <MenuItem value="info">Info (Blue)</MenuItem>
                    <MenuItem value="success">Success (Green)</MenuItem>
                    <MenuItem value="warning">Warning (Orange)</MenuItem>
                    <MenuItem value="error">Critical (Red)</MenuItem>
                </Select>
            </Grid>
            <Grid item xs={3} md={2}>
                <Button 
                    fullWidth 
                    variant="contained" 
                    onClick={handleSendBroadcast}
                    disabled={broadcastLoading}
                >
                    {broadcastLoading ? 'Sending...' : 'Broadcast'}
                </Button>
            </Grid>
            <Grid item xs={3} md={2}>
                <Button 
                    fullWidth 
                    variant="outlined" 
                    color="error"
                    onClick={handleClearBroadcast}
                >
                    Clear Active
                </Button>
            </Grid>
        </Grid>
      </Box>

      {/* Quick Actions */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              fullWidth
              sx={{ 
                py: 2, 
                borderColor: '#a8851d', 
                color: '#a8851d',
                '&:hover': { borderColor: '#8c6e18', bgcolor: '#fff9f0' }
              }}
              onClick={() => navigate('/super-admin/dealers')}
            >
              Manage Dealers
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              fullWidth
              sx={{ 
                py: 2, 
                borderColor: '#388e3c', 
                color: '#388e3c',
                '&:hover': { borderColor: '#2e7d32', bgcolor: '#f1f8f4' }
              }}
              onClick={() => navigate('/super-admin/surveys')}
            >
              View All Surveys
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              fullWidth
              sx={{ 
                py: 2, 
                borderColor: '#f57c00', 
                color: '#f57c00',
                '&:hover': { borderColor: '#e65100', bgcolor: '#fff8f0' }
              }}
              onClick={() => navigate('/super-admin/responses')}
            >
              View All Responses
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              fullWidth
              sx={{ 
                py: 2, 
                borderColor: '#7b1fa2', 
                color: '#7b1fa2',
                '&:hover': { borderColor: '#6a1b9a', bgcolor: '#f9f0fa' }
              }}
              onClick={() => navigate('/super-admin/images')}
            >
              Export Images (AI Training)
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              fullWidth
              sx={{ 
                py: 2, 
                borderColor: '#d32f2f', 
                color: '#d32f2f',
                '&:hover': { borderColor: '#b71c1c', bgcolor: '#ffebee' }
              }}
              onClick={() => navigate('/super-admin/quotes')}
            >
              Global Quote Pipeline
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Confirm Broadcast
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will show to <strong>ALL logged-in dealers</strong> immediately and remain visible for 48 hours.
          </DialogContentText>
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Preview:
            </Typography>
            <Alert severity={broadcastType} sx={{ mt: 1 }}>
              {broadcastMsg}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setConfirmDialogOpen(false)}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmSendBroadcast}
            variant="contained"
            color="primary"
            autoFocus
          >
            Send Broadcast
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={clearDialogOpen}
        onClose={() => setClearDialogOpen(false)}
      >
        <DialogTitle>Remove Active Banner?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will immediately remove the banner from all dealer dashboards.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={confirmClearBroadcast}
            color="error"
            variant="contained"
          >
            Remove Banner
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default SuperAdminDashboard;